/**
 * Market tab — dedicated oscillator detail page (spec §11.2).
 *
 * Hero card with current value, zone, direction, sparkline, overlay flags,
 * and base-rate readout; full chart below with zone bands and range toggle.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Info,
  Flag,
  Thermometer,
  Clock as ClockIcon,
  Target,
  History as HistoryIcon,
  AlertTriangle,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  CartesianGrid,
} from 'recharts';
import {
  getOscillatorCurrent,
  getOscillatorHistory,
} from '../api/client';
import Sparkline from './Sparkline';
import OscillatorExplainModal from './OscillatorExplainModal';
import {
  zoneForValue,
  canonicalZone,
  zoneLabel,
  zoneTokens,
  directionGlyph,
} from '../lib/oscillatorZone';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const RANGE_OPTIONS = [
  { key: '30d', days: 30 },
  { key: '90d', days: 90 },
  { key: '1yr', days: 365 },
  { key: '5yr', days: 1825 },
];

function fmtValue(v, digits = 1) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}`;
}

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v) * 100;
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function describeOverlay(reading) {
  const items = [];
  const divergence = reading.divergence_state;
  if (divergence && divergence !== 'confirming' && divergence !== 'none') {
    const tone =
      divergence === 'bullish_divergence' || divergence === 'bullish'
        ? 'oversold'
        : divergence === 'bearish_divergence' || divergence === 'bearish'
        ? 'overbought'
        : 'neutral';
    const label =
      divergence === 'bullish_divergence' || divergence === 'bullish'
        ? 'Bullish divergence (SPX down, oscillator up)'
        : divergence === 'bearish_divergence' || divergence === 'bearish'
        ? 'Bearish divergence (SPX up, oscillator down)'
        : `Divergence: ${divergence}`;
    items.push({ icon: Flag, tone, text: label });
  } else if (divergence === 'confirming') {
    items.push({ icon: Flag, tone: 'neutral', text: 'Oscillator and SPX moving in sync' });
  }

  if (reading.regime) {
    const tone =
      reading.regime === 'risk_on'
        ? 'oversold'
        : reading.regime === 'risk_off'
        ? 'overbought'
        : 'neutral';
    const vix = reading.vix_level != null ? ` (VIX ${Number(reading.vix_level).toFixed(1)})` : '';
    items.push({
      icon: Thermometer,
      tone,
      text: `Regime: ${reading.regime.replace('_', ' ')}${vix}`,
    });
  }

  if (reading.catalyst_proximity) {
    items.push({
      icon: ClockIcon,
      tone: 'getting_extended',
      text: typeof reading.catalyst_proximity === 'string'
        ? reading.catalyst_proximity
        : `Catalyst nearby: ${JSON.stringify(reading.catalyst_proximity)}`,
    });
  } else {
    items.push({
      icon: ClockIcon,
      tone: 'neutral',
      text: 'No major catalyst in proximity window',
    });
  }

  if (reading.deployment_guidance) {
    const dg = reading.deployment_guidance;
    const label = typeof dg === 'string'
      ? dg
      : dg.label || dg.summary || 'Deployment guidance available';
    items.push({ icon: Target, tone: 'oversold', text: label });
  }

  return items;
}

function renderBaseRates(br) {
  if (!br) return null;
  if (br.status === 'insufficient_history') {
    return (
      <p className="text-xs text-slate-500">
        Base-rate lookup requires ≥{br.min_observations ?? 50} observations per horizon; bucket has only {Object.values(br.horizons || {}).map((h) => h.n).join('/') || '—'}. Data will accrue as the scheduler persists daily readings.
      </p>
    );
  }
  if (!br.horizons) return null;
  return (
    <div className="text-sm text-slate-300">
      <p className="text-xs uppercase text-slate-500 tracking-wide mb-2">Forward return base rates (bucket {br.bucket})</p>
      <div className="grid grid-cols-3 gap-3">
        {Object.entries(br.horizons).map(([h, stats]) => (
          <div key={h} className="border border-slate-700 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase">{h} forward</p>
            {stats.status === 'insufficient_history' ? (
              <p className="text-xs text-slate-500 mt-1">Insufficient history (n={stats.n})</p>
            ) : (
              <>
                <p className="font-mono text-emerald-300 text-sm mt-1">
                  {stats.pct_positive != null ? `${(stats.pct_positive * 100).toFixed(0)}% positive` : '—'}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  median {stats.median != null ? fmtPct(stats.median) : '—'} · n={stats.n}
                </p>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Hero card
// -----------------------------------------------------------------------------

function HeroCard({ reading, sparklineData, onExplain, onHistory }) {
  const zone = canonicalZone(reading.zone, reading.oscillator_value);
  const tokens = zoneTokens(zone);
  const direction = (() => {
    if (sparklineData.length < 2) return null;
    const prev = sparklineData[sparklineData.length - 2];
    const curr = sparklineData[sparklineData.length - 1];
    if (curr > prev) return 'up';
    if (curr < prev) return 'down';
    return 'flat';
  })();
  const prevValue = sparklineData.length >= 2 ? sparklineData[sparklineData.length - 2] : null;
  const overlayItems = describeOverlay(reading);

  return (
    <div className={`rounded-2xl border ${tokens.borderClass} ${tokens.bgSoft} p-6`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className={`w-5 h-5 ${tokens.textClass}`} />
          <h2 className="text-sm uppercase tracking-widest font-semibold text-slate-300">
            S&P Breadth Oscillator
          </h2>
        </div>
        <button
          onClick={onExplain}
          className="p-1.5 text-slate-500 hover:text-slate-200 transition-colors"
          title="Explain reading"
        >
          <Info className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap items-baseline gap-4 mb-4">
        <span className={`font-mono text-5xl font-bold ${tokens.textClass}`}>
          {fmtValue(reading.oscillator_value)}
        </span>
        <div>
          <p className={`text-lg font-semibold uppercase tracking-wide ${tokens.textClass}`}>
            {zoneLabel(zone)}
          </p>
          {direction && prevValue != null && (
            <p className="text-xs text-slate-400">
              {directionGlyph(direction)} from {fmtValue(prevValue)} prior
            </p>
          )}
        </div>
        <div className="ml-auto">
          <Sparkline data={sparklineData} width={180} height={40} />
        </div>
      </div>

      <ul className="space-y-2 mb-5">
        {overlayItems.map((item, idx) => {
          const itemTokens = zoneTokens(item.tone || 'neutral');
          const Icon = item.icon;
          return (
            <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
              <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${itemTokens.textClass}`} />
              <span>{item.text}</span>
            </li>
          );
        })}
      </ul>

      {renderBaseRates(reading.base_rates)}

      <div className="mt-5 flex gap-2">
        <button
          onClick={onExplain}
          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-200 transition-colors"
        >
          Explain reading
        </button>
        <button
          onClick={onHistory}
          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-200 transition-colors flex items-center gap-1"
        >
          <HistoryIcon className="w-3.5 h-3.5" /> View history
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Oscillator chart with zone bands (§11.2 secondary content)
// -----------------------------------------------------------------------------

function OscillatorChart({ records, range, onRangeChange }) {
  const chartData = useMemo(
    () =>
      (records || []).map((r) => ({
        date: r.trade_date,
        oscillator: r.oscillator_value,
      })),
    [records]
  );

  return (
    <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
          Oscillator history
        </h3>
        <div className="flex gap-1">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => onRangeChange(opt.days)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                range === opt.days
                  ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
              }`}
            >
              {opt.key}
            </button>
          ))}
        </div>
      </div>

      {chartData.length < 2 ? (
        <div className="h-72 flex items-center justify-center text-sm text-slate-500">
          Not enough history to plot.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
            <XAxis
              dataKey="date"
              stroke="#64748b"
              fontSize={11}
              minTickGap={40}
            />
            <YAxis
              stroke="#64748b"
              fontSize={11}
              domain={[-10, 10]}
              ticks={[-10, -7, -5, -3, 0, 3, 5, 7, 10]}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                color: '#e2e8f0',
                fontSize: '12px',
              }}
              formatter={(v) => [Number(v).toFixed(3), 'Oscillator']}
              labelFormatter={(d) => d}
            />
            {/* Extreme zone bands */}
            <ReferenceArea y1={7} y2={10} fill={zoneTokens('extreme_overbought').fill} fillOpacity={1} />
            <ReferenceArea y1={5} y2={7} fill={zoneTokens('overbought').fill} fillOpacity={1} />
            <ReferenceArea y1={-7} y2={-5} fill={zoneTokens('oversold').fill} fillOpacity={1} />
            <ReferenceArea y1={-10} y2={-7} fill={zoneTokens('extreme_oversold').fill} fillOpacity={1} />
            {/* Threshold guidelines */}
            <ReferenceLine y={0} stroke="#64748b" strokeDasharray="2 4" />
            <ReferenceLine y={3} stroke="#f59e0b" strokeDasharray="2 4" opacity={0.5} />
            <ReferenceLine y={-3} stroke="#14b8a6" strokeDasharray="2 4" opacity={0.5} />
            <Line
              type="monotone"
              dataKey="oscillator"
              stroke="#e2e8f0"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Weight breakdown table
// -----------------------------------------------------------------------------

const INPUT_LABELS = {
  ad_line: 'Advance/Decline line',
  up_down_vol: 'Up/Down volume ratio',
  new_highs_lows: 'New highs − lows',
  pct_above_20sma: '% above 20-day SMA',
  spx_5d_roc: 'SPX 5-day ROC',
};

function WeightBreakdown({ reading }) {
  const rawInputs = reading.raw_inputs || {};
  const zScores = reading.z_scores || {};
  const weights = reading.weights_used || {};
  const SCALE = 4.0;
  const rows = Object.keys(INPUT_LABELS).map((key) => {
    const z = Number(zScores[key] ?? 0);
    const w = Number(weights[key] ?? 0);
    const contribution = w * z * SCALE;
    return {
      key,
      label: INPUT_LABELS[key],
      raw: rawInputs[key],
      z,
      weight: w,
      contribution,
    };
  });
  return (
    <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-6">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-300 mb-3">
        Today's input contributions
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase text-slate-500 border-b border-slate-800">
              <th className="text-left py-2 pr-4">Input</th>
              <th className="text-right py-2 px-2 font-mono">Raw</th>
              <th className="text-right py-2 px-2 font-mono">z</th>
              <th className="text-right py-2 px-2 font-mono">Weight</th>
              <th className="text-right py-2 pl-2 font-mono">Contribution</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-slate-800/50 last:border-0">
                <td className="py-2 pr-4 text-slate-300">{r.label}</td>
                <td className="py-2 px-2 text-right font-mono text-slate-400">
                  {r.raw == null ? '—' : Number(r.raw).toFixed(4)}
                </td>
                <td className="py-2 px-2 text-right font-mono text-slate-300">
                  {fmtValue(r.z, 2)}
                </td>
                <td className="py-2 px-2 text-right font-mono text-slate-400">
                  {r.weight.toFixed(2)}
                </td>
                <td className={`py-2 pl-2 text-right font-mono font-semibold ${r.contribution >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {fmtValue(r.contribution, 3)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main export
// -----------------------------------------------------------------------------

export default function Market() {
  const [current, setCurrent] = useState({ status: 'loading', reading: null });
  const [history, setHistory] = useState({ status: 'loading', records: [] });
  const [range, setRange] = useState(90);
  const [explainOpen, setExplainOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getOscillatorCurrent();
        if (cancelled) return;
        if (!resp.available) {
          setCurrent({ status: 'empty', reading: null });
        } else {
          setCurrent({ status: 'ready', reading: resp.reading });
        }
      } catch (err) {
        if (!cancelled) {
          setCurrent({ status: 'error', reading: null, error: err });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setHistory((h) => ({ ...h, status: 'loading' }));
    (async () => {
      try {
        const resp = await getOscillatorHistory(range);
        if (cancelled) return;
        setHistory({ status: 'ready', records: resp.records || [] });
      } catch (err) {
        if (!cancelled) {
          setHistory({ status: 'error', records: [], error: err });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  const sparklineData = useMemo(() => {
    return (history.records || [])
      .slice(-30)
      .map((r) => Number(r.oscillator_value))
      .filter((v) => !Number.isNaN(v));
  }, [history.records]);

  if (current.status === 'loading') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (current.status === 'empty') {
    return (
      <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-12 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
        <p className="text-slate-300 text-lg">No oscillator readings available yet.</p>
        <p className="text-sm text-slate-500 mt-1">
          The scheduler publishes a new reading each trading day at 5 PM ET.
        </p>
      </div>
    );
  }

  if (current.status === 'error') {
    return (
      <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
        Failed to load oscillator: {current.error?.message || 'unknown error'}
      </div>
    );
  }

  const reading = current.reading;

  return (
    <div className="space-y-6">
      <HeroCard
        reading={reading}
        sparklineData={sparklineData}
        onExplain={() => setExplainOpen(true)}
        onHistory={() => setRange(365)}
      />

      <OscillatorChart
        records={history.records}
        range={range}
        onRangeChange={setRange}
      />

      <WeightBreakdown reading={reading} />

      {explainOpen && (
        <OscillatorExplainModal
          date={reading.trade_date}
          onClose={() => setExplainOpen(false)}
        />
      )}
    </div>
  );
}
